import type { EventStatus, Visibility } from '@/config/objects/events';
import { getTimezoneOptions } from '@/lib/datetime';
import { cn } from '@/lib/utils';
import { useEventForm } from '@/pages/events/useEventForm';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DatePicker,
  FormFieldGroup,
  FormPageLayout,
  ImageUploadDropzone,
  Input,
  RichTextEditor,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  TimeInput,
  joinDatetimeLocalValue,
  splitDatetimeLocalValue,
  toast,
  VideoUploadDropzone,
} from '@startupquickstart/frontend';
import { AlertTriangle, ArrowLeft, Save } from 'lucide-react';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const TIMEZONE_OPTIONS = getTimezoneOptions();

/**
 * Create / edit the essentials of an event (identity, media, schedule, status,
 * visibility). Branding + map icons are configured on the event's Branding
 * tab, not here, keeping this form short. On create the status is fixed to
 * draft (advance it via the lifecycle controls); editing a live event surfaces
 * a warning before schedule/status changes.
 */
export function EventFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const itemId = id === 'new' ? undefined : id;

  // A brand-new event's obvious next step is adding challenges — land there.
  const onCreated = useCallback(
    (eventId: string) => navigate(`/events/${eventId}/challenges`),
    [navigate],
  );
  const onNotFound = useCallback(() => {
    toast.error('Event not found');
    navigate('/events');
  }, [navigate]);

  const { isEdit, formData, errors, updateField, isLoading, formReady, lastSaved, isSaving, save } =
    useEventForm({ itemId, onCreated, onNotFound });

  const handleCancel = () => navigate(isEdit ? `/events/${itemId}` : '/events');
  const isLive = isEdit && (formData.status === 'active' || formData.status === 'paused');

  // Loading skeleton (edit mode, before the event has hydrated).
  if (isEdit && (isLoading || !formReady)) {
    return (
      <FormPageLayout>
        <FormPageLayout.Header>
          <div className="flex items-center gap-4">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-60" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-32" />
          </div>
        </FormPageLayout.Header>
        <FormPageLayout.Content>
          {[1, 2, 3].map((i) => (
            <Card key={i} className="rounded-xl border bg-card shadow-sm">
              <CardHeader className="px-6 py-4 !pb-4 border-b">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-64" />
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </Card>
          ))}
        </FormPageLayout.Content>
      </FormPageLayout>
    );
  }

  return (
    <FormPageLayout>
      {/* Header */}
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center h-9 w-9 rounded-lg border bg-card hover:bg-muted transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              {isEdit ? 'Edit Event' : 'Create Event'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isEdit ? formData.name || 'Update event details' : 'Create a new scavenger hunt event'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={save} loading={isSaving} icon={Save}>
            Save Changes
          </Button>
        </div>
      </FormPageLayout.Header>

      {/* Content */}
      <FormPageLayout.Content>
        <div className="space-y-6">
          {/* Live-event guard. Editing schedule/status while teams are playing
              can disrupt them — steer admins to the lifecycle controls. */}
          {isLive && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
              <div className="text-sm">
                <p className="font-medium">This event is live.</p>
                <p>
                  Changing the schedule or status here can disrupt teams in progress. To pause,
                  resume, or end the event, use the lifecycle controls on the event page.
                </p>
              </div>
            </div>
          )}

          {/* Event Details */}
          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <CardTitle>Event Details</CardTitle>
              <CardDescription>Basic information about the event</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <FormFieldGroup name="name" label="Name" required error={errors.name}>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Enter event name"
                  className={cn(errors.name && 'border-destructive')}
                />
              </FormFieldGroup>

              <FormFieldGroup name="description" label="Description">
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => updateField('description', value ?? '')}
                />
              </FormFieldGroup>

              <ImageUploadDropzone
                targetAspectRatio={4 / 3}
                value={formData.coverImageId}
                onChange={(mediaId) => updateField('coverImageId', mediaId)}
                label="Cover Image"
              />

              <VideoUploadDropzone
                value={formData.introVideoId}
                onChange={(mediaId) => updateField('introVideoId', mediaId)}
                label="Intro Video"
              />
            </CardContent>
          </Card>

          {/* Schedule */}
          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <CardTitle>Schedule</CardTitle>
              <CardDescription>When the event takes place</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <FormFieldGroup name="startTime" label="Start Time">
                  <DateTimeFields
                    value={formData.startTime}
                    onChange={(v) => updateField('startTime', v)}
                  />
                </FormFieldGroup>

                <FormFieldGroup name="endTime" label="End Time" error={errors.endTime}>
                  <DateTimeFields
                    value={formData.endTime}
                    onChange={(v) => updateField('endTime', v)}
                  />
                </FormFieldGroup>
              </div>

              <FormFieldGroup
                name="timezone"
                label="Timezone"
                description="Wall-clock times for this event are interpreted in this zone"
              >
                <Select value={formData.timezone} onValueChange={(v) => updateField('timezone', v)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.menuLabel}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormFieldGroup>
            </CardContent>
          </Card>

          {/* Settings — visibility always; status only when editing (new events
              start as draft and advance via the lifecycle controls). */}
          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <CardTitle>Settings</CardTitle>
              <CardDescription>Event visibility{isEdit ? ' and status' : ''}</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {isEdit && (
                  <FormFieldGroup
                    name="status"
                    label="Status"
                    description="Prefer the lifecycle controls on the event page over editing this directly"
                  >
                    <Select
                      value={formData.status}
                      onValueChange={(v) => updateField('status', v as EventStatus)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="paused">Paused</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormFieldGroup>
                )}

                <FormFieldGroup name="visibility" label="Visibility">
                  <Select
                    value={formData.visibility}
                    onValueChange={(v) => updateField('visibility', v as Visibility)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select visibility" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="private">Private</SelectItem>
                      <SelectItem value="unlisted">Unlisted</SelectItem>
                      <SelectItem value="public">Public</SelectItem>
                    </SelectContent>
                  </Select>
                </FormFieldGroup>
              </div>
            </CardContent>
          </Card>

          {isEdit && (
            <p className="text-sm text-muted-foreground">
              Logo, colors, theme, and map icons live on the event&apos;s{' '}
              <button
                type="button"
                className="font-medium text-primary underline-offset-2 hover:underline"
                onClick={() => navigate(`/events/${itemId}/branding`)}
              >
                Branding tab
              </button>
              .
            </p>
          )}
        </div>
      </FormPageLayout.Content>

      {/* Footer */}
      <FormPageLayout.Footer lastSaved={lastSaved}>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={save} loading={isSaving} icon={Save}>
            Save Changes
          </Button>
        </div>
      </FormPageLayout.Footer>
    </FormPageLayout>
  );
}

/**
 * Pair of <DatePicker> + <TimeInput> backed by a single internal
 * "datetime-local"-shaped string (`YYYY-MM-DDTHH:mm`). The shared pickers
 * operate in plain local terms (no timezone math) — the form interprets that
 * wall-clock through the event's IANA timezone when serialising.
 */
function DateTimeFields({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { date, time } = splitDatetimeLocalValue(value);
  return (
    <div className="flex gap-2">
      <div className="flex-1 min-w-0">
        <DatePicker
          date={date}
          onDateChange={(d) => onChange(joinDatetimeLocalValue(d, time || '00:00'))}
          useUtc={false}
          placeholder="Pick a date"
        />
      </div>
      <div className="shrink-0">
        <TimeInput value={time} onChange={(t) => onChange(joinDatetimeLocalValue(date, t))} />
      </div>
    </div>
  );
}
